// ── docs · concepts (and the developers' repo page) ────────────────────────
// How the memory behaves underneath: what an ask gets back, how a branch
// works, what is known about the person and what never leaves, how forgetting
// works, and how a repo keeps its memory current. Each page is written against
// the handlers (lib/mcp/handlers.ts · forget.ts · compiler.ts · foldCore.ts),
// so when one of those changes, the page it names changes with it.

import type { DocPageDef } from '../content'

export const CONCEPT_PAGES: DocPageDef[] = [
  {
    slug: 'retrieval',
    title: 'retrieval',
    group: 'concepts',
    description: 'how one question becomes the few sections that answer it, inside a hard budget.',
    updated: '2026-09-29',
    body: `
A memory is only as useful as what reaches the conversation. innernet never hands an AI the whole map and hopes. Every ask goes through one door, \`innernet_context\`, and comes back as a small, ranked package with a receipt.

## the one call

\`\`\`json
{ "name": "innernet_context", "arguments": { "slug": "my-product", "intent": "what did we decide about pricing?" } }
\`\`\`

\`intent\` is the question **in the person's own words**. Not a keyword list, not "pricing": the whole ask. The selector reads it the way a person would, and a precise question gets a precise slice. Over REST it is the same handler:

\`\`\`bash
curl "https://innernet.live/api/v1/context?slug=my-product&intent=what+did+we+decide+about+pricing" \\
  -H "Authorization: Bearer innernet_..."
\`\`\`

## what comes back

| field | what it is |
| --- | --- |
| \`read_first\` | One line: answer from focus, or the memory holds nothing on this. It ends by saying what time it is for the person, in their own timezone. Read it before anything else. |
| \`now\` | The moment on the person's own clock: \`{ date, local, tz, known_tz }\`. When they keep no timezone it is UTC, and \`known_tz\` says it is a guess. |
| \`focus\` | The sections that answer the ask, best first: \`{ dimension, section, text }\`. A section too long to fit is excerpted and marked \`partial\`. A section that writes a date of its own carries \`dated\`: the date it speaks from and how long ago that is. When it holds numbers and that date is a month or more gone, it is marked \`snapshot\`: that day's reading, not today's. Every section also carries \`standing\`: what kind of memory it is, how age has touched it, and, when it is stale or superseded, one line on what to do before using it as the present (see [how memory ages](#how-memory-ages)). |
| \`periphery\` | A handful of near-misses, \`{ ref, summary }\`: close enough to mention, not close enough to send whole. |
| \`retrieval_advice\` | What to open next if the conversation turns (usually an \`innernet_get_dimension\` call). |
| \`spine\` | What this project is: name, tagline, type, its core, open tasks, what moved lately, and \`as_of\`. |
| \`map\` | Every dimension by name, so nothing in the memory is out of reach. |
| \`personal_context\` | Only when facts about the person bear on this ask, and only shareable ones. Each fact says when it was last confirmed (\`as_of\`), and \`standing\` when its kind has aged it. |
| \`sent\` | The receipt: which selector chose the slice, how many tokens went, how many personal facts were shared, how many private ones were withheld. |
| \`meta\` | \`holds_answer\`, \`asks_about_past\`, \`to_confirm\` (the sections in focus that are stale or superseded), and the counts behind the receipt. |

## the budget

Focus has a hard ceiling of about **1,600 tokens**. The package is bounded by the ask, never by the size of the memory: a map with nine hundred sections answers in the same one to two thousand tokens as a small one. That is what keeps innernet cheap to call on every turn, and it is why a good \`intent\` matters more than a big context window.

## when the memory holds nothing

\`meta.holds_answer: false\` and a \`read_first\` that says so. That is an answer, not a failure: the AI should say the memory has nothing on it rather than guess from the project name. The periphery still lists what was close, so the conversation can go there instead.

## history stays out of the way

Every dimension keeps its superseded decisions under a \`## history\` heading. Retrieval leaves those lines out of focus, because an AI that reads "was X, now Y" next to "Y" tends to repeat the X. When the ask is about the past ("what did we use before?", "why did we change…"), \`meta.asks_about_past\` flips and the history of every admitted dimension rides along. See [branches & history](/docs/branches).

## how memory ages

Not every memory gets old the same way. A law written in june is as true in october. A number read on 24 july is a reading of 24 july. So innernet gives every section a **kind**, and every kind its own curve:

| kind | half-life | floor | what age does |
| --- | --- | --- | --- |
| law | never | 1.00 | nothing: a law stands until it is replaced |
| decision | 365 days | 0.75 | fades slowly, holds until a newer decision |
| reference | 180 days | 0.70 | how a thing works drifts as it is rebuilt |
| note | 90 days | 0.80 | everything else |
| plan | 60 days | 0.50 | a bet on a future that arrives |
| status | 14 days | 0.35 | where things stand is true for days |
| count | 10 days | 0.30 | a number is true on the day it was read |

A section's age is the newest date it writes itself (never a \`## history\` line); else the date its page's opening declares (*rewritten 2026-07-24*, *as of …*); else, most weakly, when its page was last saved. Its **state** follows: \`standing\` (a law), \`current\` (inside one half-life), \`aging\` (one to three), \`stale\` (past three: confirm it before saying it is still so), \`superseded\` (a newer reading on the same subject exists: read that first), or \`undated\`.

**Age orders, it never hides.** Relevance alone decides what reaches focus. The weight only orders what was admitted, so under the fixed budget the present is packed before the past. When the ask is about the past, every weight is 1.

**A newer reading supersedes an older one.** A page that says *the present reading is \[\[x\]\]* hands its numbers and statuses over to x, and *superseded by \[\[x\]\]* hands over its plans too. A section that says it *replaces* a page supersedes that page's older readings, and two dated readings on the same subject, two weeks or more apart, softly do the same. When an older reading reaches focus, the one that replaced it takes its place in the order, and the older one follows at its aged weight. Laws and decisions are never superseded by a reading: they are replaced by rewriting them, and \`## history\` keeps the old one.

**Readings are dated at birth.** When a page is saved, by you or by netti, a section that ages fast (a count, a status, a plan), writes no date, and changed in this save gets one quiet line at its end: \`_as of 2026-10-08_\`, on your own calendar. An unchanged section keeps what it had.

**You can say what a section is.** Put \`<!-- ages: law -->\` anywhere in a section (it does not render), or \`ages: decision\` in a page's frontmatter. Otherwise innernet reads the kind from the section's heading (a plan full of percentage targets is still a plan), then from numbers in its text, then from the page's name.

Facts about you age the same way, by what they are about: who you are stands, how you work holds for a long time, what you use drifts, and what you are in the middle of goes stale in weeks.

## captures that haven't folded yet

A capture lands in the commit log in milliseconds; netti folds it into dimensions afterwards. Until it does, retrieval splices recent captures in as a \`pending_captures\` section on projects you own, so something you said a minute ago is already answerable.

## lean loads, full opens

\`innernet_load_project\` is lean by default: one line per dimension, the capture protocol and a slice of personal context. It orients a session. \`innernet_get_dimension\` opens one page whole when the conversation turns that way, and \`full: true\` on a load returns every body (the REST \`GET /projects/{slug}\` always does). For answering questions, \`innernet_context\` is almost always the right call.

## related

- [The Context Map model](/docs/concepts)
- [Every MCP tool](/docs/mcp-tools)
- [Capture protocol](/docs/capture-protocol)
`,
  },

  {
    slug: 'branches',
    title: 'branches & history',
    group: 'concepts',
    description: 'fork the whole memory, try a direction, diff it three-way, merge what won. and the one rule that keeps trunk readable.',
    updated: '2026-09-29',
    body: `
Code has had branches for decades. Thinking hasn't. innernet gives a memory the same two tools: **branches**, for holding two versions of an idea on purpose, and **history**, for knowing how the current version came to be.

## the one rule on trunk

A dimension body describes **the present**. When something is superseded, netti replaces it in the body and appends one dated line under \`## history\` at the end of that dimension:

\`\`\`markdown
## history
- 2026-09-12 — pricing: was a flat $12 seat → now usage-based, $0.005 per 1k ops
- 2026-08-18 — storage: was a local store → now one hosted memory
\`\`\`

At most twelve lines stay in the ledger; the commit log keeps the rest. Retrieval leaves history out of focus unless the ask is about the past ([retrieval](/docs/retrieval)), so trunk always answers with one truth.

## when to branch

Branch when you want **two truths at once**: a different positioning, an alternative architecture, a pricing experiment, a rewrite you might abandon. Divergence lives on branches; trunk stays unambiguous.

## the lifecycle

1. **Fork.** \`innernet_branch_new { project_slug, name, summary }\` snapshots every dimension and node at the current head into an overlay.
2. **Work on it.** \`innernet_save_context { slug, branch: "name", dimensions: … }\` writes to the overlay only. Trunk is untouched, and so is everyone reading trunk.
3. **Compare.** \`innernet_branch_diff\` is a **three-way** diff against the fork point: what the branch added, modified and removed, and \`conflicts\`, the pages trunk also changed, differently, since you forked.
4. **Decide.** Merge it, park it, or keep going.

In a conversation you rarely call these by name. Say it:

\`\`\`text
Branch my-product as "usage-pricing" and rewrite the pricing page there
as usage-based. Then show me what changed against trunk.
\`\`\`

## merge, three ways

\`innernet_branch_merge\` applies **only what the branch changed**. A branch with no edits is a no-op. An edit trunk made after the fork is never overwritten. A page both sides changed differently stays as trunk has it and is listed under \`diff.conflicts\`; the result says \`merged: false\` until you resolve those pages and merge again. A merge records its own commit and moves the head.

A parked branch, or one already merged, refuses to merge.

## park, revive

\`innernet_branch_park { name }\` archives a branch with its overlay intact. \`park: false\` brings it back. Nothing on a parked branch is lost; it just stops showing up as live.

## over REST

\`\`\`bash
POST  /api/v1/projects/{slug}/branches                      # fork
PUT   /api/v1/projects/{slug}/branches/{name}/dimensions/{d} # write to the branch
GET   /api/v1/projects/{slug}/branches/{name}/diff           # three-way diff
POST  /api/v1/projects/{slug}/branches/{name}/merge          # merge
PATCH /api/v1/projects/{slug}/branches/{name}  {"status":"parked"|"active"}
GET   /api/v1/projects/{slug}/commits                        # the log
\`\`\`

Examples with full bodies are on the [API page](/docs/api#version-everything-commits-branches).

## commits

Every save, capture, fold and merge is a commit with a hash, a parent, a message and a trigger (\`genesis\`, \`mcp\`, \`api\`, \`fold\`, \`merge\`…). The log is append-only. It is what lets a dimension stay short: the body says what is true, \`## history\` says what changed recently, and the commits say everything else.

## related

- [Branching your memory, step by step](/community/guides/starter-branch-your-memory) (a guide)
- [The Context Map model](/docs/concepts)
`,
  },

  {
    slug: 'personal-memory',
    title: 'personal memory',
    group: 'concepts',
    description: 'the self map: what innernet knows about you, how every fact is classed, and what stays inside innernet.',
    updated: '2026-09-29',
    body: `
Separate from every project, innernet keeps one **Self Map** per account: atomic facts about *you*. The tools you rely on, how you like answers, the people you work with, when and how you work, standing instructions. Every connected AI can read the shareable part of it, so a new chat already knows how you work.

## how a fact is born

1. **A capture.** An AI hears something durable about you and calls \`innernet_self_capture\` with the note, and ideally \`user_words\`: your own sentence, copied exactly, so the fact can be evidenced by what you said rather than the AI's summary.
2. **The queue.** Captures wait as candidates. Nothing is a fact yet.
3. **The fold.** netti's consolidator (\`innernet_self_sync\`, or on its own schedule) turns candidates into facts: it merges duplicates, supersedes what changed, and keeps **every source** a fact came from, so one fact can carry many sentences behind it.
4. **A question, sometimes.** When the consolidator can't resolve something (who someone is to you, whether a preference is standing), it asks once. On clients that support MCP elicitation the question can arrive right in your chat; otherwise it waits in your inbox.

## disclosure classes

Every fact carries one of five classes, from most to least guarded:

| class | who can read it |
| --- | --- |
| \`private\` | you. No room or share link ever sees it; an AI tool reads it only when you allow that app, and each time is written down. |
| \`self\` | you. An AI tool reads it only when you allow that app, and each time is written down. |
| \`trusted\` | AI tools you have connected. The default ceiling. |
| \`work\` | tools and project loads in a work context. |
| \`public\` | anything you allow, including shares. |

Every app reads trusted, work and public facts; a tool can ask for less with a ceiling (\`innernet_self_facts { max_disclosure: "work" }\` returns work and public). **Self and private facts reach only an app you have cleared for them**, and by default no app is. Anything asked for and not cleared comes back as a \`withheld\` count, never its words, and innernet asks you. Every self or private fact that does leave is written in a ledger on your **you** page: which app, which tool, and when.

When disclosure is ambiguous, the fold biases toward the guarded side. You can move any fact up or down on the **you** page of your dashboard.

## what a project load carries

\`innernet_load_project\` and \`innernet_context\` add a \`personal_context\` slice when facts bear on the work: current facts only, confidence 0.6 or higher, in the trusted, work and public classes, at most 40. The receipt counts what was withheld (\`withheld_private_facts\`), so you can see that the gate held without seeing what it held back.

## seeing and changing it

Your dashboard's **you** space is the whole Self Map: every fact, its class, its sources, and where each source came from. A private fact opens on the moment it came from, labelled honestly. Edit a fact, change its class, or forget it there; or ask any connected AI to. [The you space](/docs/you)

## the tools

| tool | what it does |
| --- | --- |
| \`innernet_self_capture\` | queue a candidate about you (\`dimension_hint\`, \`user_words\`) |
| \`innernet_self_facts\` | read facts as far as you let this app read them; what isn't cleared comes back as a count |
| \`innernet_self_status\` | counts: facts, pending candidates, open questions, and one opaque withheld total |
| \`innernet_self_sync\` | run the fold now (can run as a long MCP task) |
| \`innernet_triage\` | ask where a note belongs: about you, about the project, both, or nowhere |
| \`innernet_forget { kind: "fact" }\` | forget one fact and every version of it, in two steps |

## related

- [Privacy & security](/docs/security)
- [Forgetting](/docs/forgetting)
- [Watching your private facts](/community/guides/starter-you-and-your-facts) (a guide)
`,
  },

  {
    slug: 'forgetting',
    title: 'forgetting',
    group: 'concepts',
    description: 'one page, one cell, one fact or a whole memory, gone in two deliberate steps.',
    updated: '2026-09-29',
    body: `
Remembering is only trustworthy if forgetting is real. innernet forgets for good, and it always asks first.

## two steps, every time

Every forget is a **preview, then a confirm**. The first call deletes nothing. It shows what would go, where it lives, a short excerpt, anything that goes with it, a link to see it on your dashboard, and a \`confirm_with\` code. The second call, carrying that code, deletes.

The code is tied to the exact thing and the moment you looked at it. If it changed since, the second call comes back with the fresh preview (\`changed_since_you_looked\`) instead of deleting something you haven't seen.

## what you can forget

| kind | what it is | what goes with it |
| --- | --- | --- |
| \`page\` | one dimension of a project you own | its earlier versions, and its community listing if it was a published skill |
| \`cell\` | one node (a person, a decision, an artifact) by id or title | the node |
| \`fact\` | one personal fact, by the id \`innernet_self_facts\` gives | every version of it, before and after, and any pattern read from it |
| a whole project | \`innernet_forget_project\` | everything: dimensions, nodes, commits, branches, artifacts, tasks, handoffs |

## in a conversation

\`\`\`text
Forget the "old-pricing" page in my-product.
\`\`\`

The AI calls \`innernet_forget\`, shows you the preview, and waits. Say yes and it calls again with the code. Asking twice is the point: an AI should never be the only one who decided.

## a whole memory

\`innernet_forget_project\` shows everything the memory holds (counts of dimensions, nodes, commits, branches, artifacts, tasks) and asks you to confirm **with the slug itself**. It is permanent. There is no bin.

If you only want a memory out of sight, **archive** it from the dashboard instead. Archiving is reversible; forgetting isn't. And if you might want it later, export first from [your account](/account).

## over REST

\`\`\`bash
POST   /api/v1/forget               { "kind": "page", "project_slug": "my-product", "name": "old-pricing" }
POST   /api/v1/forget               { …same…, "confirm": "3fa9c21e" }
DELETE /api/v1/projects/my-product  # then again with ?confirm=my-product
\`\`\`

The dashboard's forget buttons call the very same function, so a forget is a forget wherever it starts.

## what forgetting cannot reach

Once an AI client has read something, it lives in that provider's conversation and possibly its logs. innernet can forget its copy; it can't reach into theirs. [Privacy & security](/docs/security) says what we promise and what we don't.
`,
  },

  {
    slug: 'repo-memory',
    title: 'repo memory',
    group: 'for developers',
    description: 'every commit captured, every pull folded, one memory per repo, inherited on clone.',
    updated: '2026-09-29',
    body: `
Run \`npx innernet\` inside a git repo and that repo gets a memory of its own, kept current by the commits you were making anyway.

## what lands in the repo

\`\`\`text
.innernet/
  connection.json   # the memory's slug, nothing secret. committed.
  hooks/
    post-commit     # every commit → one capture
    post-merge      # every pull or merge → the commits it brought
  .gitignore        # keeps last-sync.log out of git
AGENTS.md           # a short block: where the memory is, the four calls
CLAUDE.md           # @AGENTS.md, so Claude Code reads the same words
\`\`\`

\`core.hooksPath\` is set to \`.innernet/hooks\` for this clone. If the repo already uses its own hooks path, \`npx innernet\` prints the lines to add instead of taking it over.

## the loop

1. **You commit.** \`post-commit\` sends the message and the diffstat as a capture. It runs in the background; it can never slow a commit down, and a failed send never fails the commit.
2. **You pull.** \`post-merge\` captures what arrived: first-parent commits from \`ORIG_HEAD\` to \`HEAD\`, oldest first, up to 40, tagged as coming from a pull.
3. **netti folds.** On the server, captures since the last fold are consolidated into the memory's dimensions. The body describes the present; superseded decisions move under \`## history\`.
4. **Any AI reads it.** The next session in Claude Code, Cursor or Codex asks \`innernet_context\` and gets the current picture, including why the last three changes happened.

Write better commit messages and the memory gets better. That is the whole trick.

## the repo view

On the dashboard, a memory tied to a repo shows its repo line: branch, short sha and the last subject, and the commits as they were captured. The [chat window](/docs/chat-window) shows the same line inside your AI chat.

## teammates

\`connection.json\` is committed, so the link travels with the code. Share the memory with a teammate (a room, see [sharing](/docs/sharing)); they clone, run \`npx innernet\`, and read the same memory. If their account can't see it yet, the command says so and tells them to ask the owner for room access, rather than quietly starting a second memory.

## handoffs

Long work rarely ends in one session. \`innernet_handoff_write\` leaves "where I left off" for the next session, whoever or whatever it is; \`innernet_handoff_read\` picks it up. Every write is kept (\`history: true\` shows the trail), and a \`scope\` keeps parallel streams apart. The \`AGENTS.md\` block asks every agent to write one at the end.

## CI and scripts

\`\`\`bash
INNERNET_API_KEY=innernet_… npx innernet capture "deployed v2.4 to production" -s ci -t deploy
\`\`\`

A key (minted under **connections → api**) replaces the browser sign-in. \`capture\` finds the memory from \`.innernet/connection.json\` in the working directory, or \`-p <dir>\`.

## related

- [The npx command](/docs/cli)
- [For developers](/docs/for-developers)
- [Managing your repo memory](/community/guides/starter-your-repo-in-innernet) (a guide)
`,
  },
]
